"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
import { ArrowRight } from "lucide-react";
const ladderSteps = [{
  title: "Free Strategy Call",
  description: "Discover your brand's potential and identify immediate growth opportunities with a complimentary assessment."
}, {
  title: "Creative Strategy Consultation",
  description: "Get a customized roadmap tailored to your unique creative vision, designed to bridge the gap between ideas and execution."
}, {
  title: "Brand Development",
  description: "Build a cohesive, high-end brand identity that naturally attracts premium clients and stands out in a crowded market."
}, {
  title: "Digital Marketing Solutions",
  description: "Implement scalable, automated marketing systems designed to consistently generate qualified leads and nurture relationships."
}, {
  title: "VIP Business Program",
  description: "Our exclusive $9,997 intensive for founders who prefer to let us take over to scale their impact and income."
}];
function LadderStep({
  step,
  index
}: {
  step: (typeof ladderSteps)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const isLeft = index % 2 === 0;
  return <div ref={ref} data-index={index} className="relative flex items-center w-full mb-16 last:mb-0 group">
      {}
      <div className={cn("absolute left-8 md:left-1/2 w-5 h-5 rounded-full border-4 border-background bg-accent -translate-x-1/2 z-10 transition-all duration-500 delay-200 shadow-sm", isVisible ? "scale-100 opacity-100" : "scale-0 opacity-0")} />

      {}
      <div className={cn("ml-20 md:ml-0 w-[calc(100%-5rem)] md:w-[calc(50%-4rem)] transition-all duration-700 ease-out", isLeft ? "md:mr-auto md:pr-4" : "md:ml-auto md:pl-4", isVisible ? "opacity-100 translate-x-0 translate-y-0" : "opacity-0 translate-y-8 md:translate-y-0", !isVisible && isLeft ? "md:-translate-x-12" : "", !isVisible && !isLeft ? "md:translate-x-12" : "")}>
        <Card className="shadow-sm hover:shadow-md transition-shadow duration-300 border-border/40 bg-card/80 backdrop-blur-sm relative overflow-hidden">
          {}
          <div className="absolute top-0 left-0 w-1 h-full bg-accent/20 transition-colors duration-300 group-hover:bg-accent" />
          <CardHeader className="pb-4">
            <Badge variant="secondary" className="w-fit mb-3 text-accent bg-accent/10 hover:bg-accent/20 border-transparent font-sans tracking-wide uppercase text-[10px]">
              Tier {index + 1}
            </Badge>
            <CardTitle className="font-serif text-xl md:text-2xl text-foreground leading-tight">
              {step.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground leading-relaxed text-sm md:text-base font-sans">
              {step.description}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>;
}
export default function TheGrowthLadder() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="the-growth-ladder" className="relative bg-background py-24 md:py-32 overflow-hidden z-0">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10 pointer-events-none">
        <div className="absolute top-1/4 -left-64 w-[40rem] h-[40rem] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-64 w-[40rem] h-[40rem] bg-secondary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 md:px-6 max-w-5xl">
        {}
        <div className="text-center max-w-2xl mx-auto mb-20 md:mb-28">
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground mb-6 tracking-tight">
            Your Journey to Recognized Expert
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl font-sans leading-relaxed">
            A proven 5-step journey from creative struggle to strategic success.
            We meet you where you are and guide you to where you want to be.
          </p>
        </div>

        {}
        <div ref={sectionRef} className="relative w-full max-w-4xl mx-auto">
          {}
          <div className="absolute left-8 md:left-1/2 top-4 bottom-4 w-px bg-border -translate-x-1/2" />

          {}
          <div className={cn("absolute left-8 md:left-1/2 top-4 w-px bg-primary -translate-x-1/2 transition-all duration-[2000ms] ease-in-out origin-top", sectionVisible ? "scale-y-100" : "scale-y-0")} style={{
          height: "calc(100% - 2rem)"
        }} />

          {}
          <div className="relative z-10 py-4">
            {ladderSteps.map((step, index) => <LadderStep key={index} step={step} index={index} />)}
          </div>
        </div>

        {}
        <div className="mt-20 text-center">
          <Button asChild size="lg" className="rounded-full px-8 py-6 text-base shadow-md hover:shadow-lg transition-all">
            <Link to="/ContactPage">
              Start Your Journey Today
              <ArrowRight className="ml-2 size-5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}